/**
 * buildingYears — a build year for the listed buildings whose own register
 * publishes none.
 *
 * England's and Wales's registers state when a building was listed, never
 * when it was built (measured 5 Sep 2026; see scripts/add-building-years.mjs
 * for what each of the six silent registers does carry). Wikidata knows the
 * list entry and Cadw numbers, and public/data/building-years.json is that
 * crossing: 16 712 English and 864 Welsh buildings, four per cent and three,
 * and the famous per cent at that. The rest keep no year and the timeline
 * draws them undated, which is what the register is actually saying.
 *
 * Keyed off the pin id, which already ends in the register number
 * (`en-bld-<ListEntry>`, `wa-bld-<RecordNumber>`), so no fetcher changes.
 * Fetched only when such a pin is on screen and has no year of its own, so
 * a reader who never opens Britain never pays the 74 kB. Called from
 * hooks/useFetchLayers.ts where the building layers are joined.
 */
import type { ProtectedBuilding } from "../types/index.ts";
import { warnFetch } from "../utils/logErr.ts";

type Table = { en?: Record<string, number>; wa?: Record<string, number> };
let table: Promise<Table> | null = null;

function loadBuildingYears(): Promise<Table> {
  table ??= fetch("/data/building-years.json")
    .then((r) => { if (!r.ok) throw new Error(`building-years: ${r.status}`); return r.json() as Promise<Table>; })
    .catch((e) => { warnFetch("building-years", e); table = null; return {} as Table; });
  return table;
}

/** `en-bld-1234567` → ["en", "1234567"], and null for any other id. */
function split(id: string): ["en" | "wa", string] | null {
  const m = /^(en|wa)-bld-(.+)$/.exec(id);
  return m ? [m[1] as "en" | "wa", m[2]] : null;
}

export async function withBuildingYears(items: ProtectedBuilding[]): Promise<ProtectedBuilding[]> {
  const wanted = items.some((b) => !b.dating && split(b.id));
  if (!wanted) return items;
  const t = await loadBuildingYears();
  if (!t.en && !t.wa) return items;
  return items.map((b) => {
    if (b.dating) return b;
    const k = split(b.id);
    const y = k && t[k[0]]?.[k[1]];
    return y ? { ...b, dating: String(y) } : b;
  });
}

/** Test seam. */
export function _resetBuildingYears(): void { table = null; }
